/**
 * Everything below the heading on a stats page: the season summary, the season's
 * figures, the career figures, and the nights behind them.
 *
 * ONE COMPONENT SO THAT My Stats AND A PUBLIC PROFILE CANNOT DRIFT. The two pages
 * differ in what they are allowed to show — a profile has no season selector and
 * never sees a hidden player's numbers — but not in how a season reads once they
 * have it. Keeping the layout in one place is the same argument lib/player-stats.js
 * makes for the figures themselves.
 *
 * The season summary distinguishes three states the server deliberately reports
 * differently, and getting them confused would misreport somebody's standing:
 *
 *   season === null      the venue has no season to be ranked in
 *   position === null    there is a season and this player has not scored in it
 *   position set         a real rank, out of a real field
 */

import StatGrid, { seasonStatList, allTimeStatList } from './StatGrid.jsx';
import TournamentHistoryList from './TournamentHistoryList.jsx';
import { ordinal } from '../../lib/placement.js';

function SeasonSummary({ season }) {
  if (!season) {
    return <p className="hd-meta">No active season at this venue.</p>;
  }

  const rank = season.position != null
    ? `${ordinal(season.position)} of ${season.playerCount}`
    : 'Unranked';

  return (
    <p className="hd-meta">
      {season.seasonName}
      {' · '}{rank}
      {' · '}{season.totalPoints} pt{season.totalPoints === 1 ? '' : 's'}
      {' · '}{season.tournamentsPlayed} week{season.tournamentsPlayed === 1 ? '' : 's'} played
    </p>
  );
}

export default function PlayerStatsBody({ venueSlug, season, allTime, tournaments }) {
  return (
    <>
      <SeasonSummary season={season} />

      {season && (
        <section className="hd-stats-section">
          <h2 className="hd-section-title">This season</h2>
          {season.position == null && season.tournamentsPlayed === 0 ? (
            <p className="hd-muted">
              No figures yet — these fill in after a first completed tournament.
            </p>
          ) : (
            <StatGrid stats={seasonStatList(season)} />
          )}
        </section>
      )}

      <section className="hd-stats-section">
        <h2 className="hd-section-title">All time</h2>
        {allTime.tournamentsPlayed === 0 ? (
          <p className="hd-muted">No completed tournaments at this venue yet.</p>
        ) : (
          <StatGrid stats={allTimeStatList(allTime)} />
        )}
      </section>

      <section className="hd-stats-section">
        <h2 className="hd-section-title">Tournament history</h2>
        <TournamentHistoryList venueSlug={venueSlug} tournaments={tournaments} />
      </section>
    </>
  );
}
